import { Form, Formik, FormikProps } from 'formik';
import React, { useMemo } from 'react';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import XGSFormSelect from 'ui-components/form/select/xgsFormSelect';
import XGSFormTextarea from 'ui-components/form/textarea/xgsFormTextarea';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import Table from 'ui-components/table/table';
import { getColumnsForDrops } from '../getColumnsForDrops';
import Loading from 'ui-components/loading/loading';
import { weekOptions } from 'app/data/static-load/model';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import { useMutation } from '@tanstack/react-query';
import { apiQueryAdapter } from 'app/data/apiQueryAdapter';
import { toast } from 'react-toastify';
import { useDeleteDrop } from '../hooks/useDrop';
import { useCarrier } from '../hooks/useCarrier';

type UpdateSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  data: {
    lane: any;
    drops: any[];
  },
  isLoading: boolean;
  terminalMap: any;
  isEditMode: boolean;
  refetchLane: () => void;
  handleAddDrop: () => void;
  refetchLaneByDay: () => void;
}

interface UpdateSidebarFormValues {
  division: string;
  dayOfWeek: string;
  description: string;
  lane: string;
  carrier: string;
  notes: string;
  drops: any[];
}

const UpdateSidebar: React.FC<UpdateSidebarProps> = ({
  isOpen,
  onClose,
  data,
  isLoading,
  terminalMap,
  isEditMode,
  refetchLane,
  refetchLaneByDay,
  handleAddDrop
}) => {

  const deleteDrop = useDeleteDrop()
  const { data: carrierData } = useCarrier()

  const updateLaneMutation = useMutation<
    any,
    Error,
    { planId: string; data: any }
  >({
    mutationFn: async ({ planId, data }) => {
      if (!data) throw new Error("Data is required");

      const response = await apiQueryAdapter.query<any[]>(
        `/manifest-management/lanes/${planId}`,
        "PUT",
        { body: data }
      );

      return response.data;
    },
    onSuccess: () => {
      refetchLaneByDay();
      toast.info("Lane updated successfully");
    },
  });

  const updateDropMutation = useMutation<
    any,
    Error,
    { dropId: string; data: any }
  >({
    mutationFn: async ({ dropId, data }) => {
      if (!data) throw new Error("Data is required");

      const response = await apiQueryAdapter.query<any[]>(
        `/manifest-management/drops/${dropId}`,
        "PUT",
        { body: data }
      );

      return response.data;
    },
    onSuccess: () => {
      refetchLane();
      toast.info("Drop updated successfully");
    },
  });

  const handleSave = async (values: UpdateSidebarFormValues) => {
    try {
      // Update lane first
      await updateLaneMutation.mutateAsync({
        planId: data?.lane?.id!,
        data: {
          division: values.division,
          dayOfWeek: parseInt(values.dayOfWeek),
          turnDescription: values.description,
          turnNumber: values.lane,
          carrierNumber: values.carrier,
          notes: values.notes,
          weekStartDate: data?.lane?.weekStartDate,
        },
      });

    } catch (err) {
      toast.error("Failed to update lane");
    }
  };


  const onSaveDrop = async (
    value: string,
    dropId: string,
    type?: "tractorNumber" | "earlyArrivalTime" | "earlyDepartureTime"
  ) => {
    try {
      const payload: any = {
        planId: data?.lane?.id,
        terminalNumber: data?.drops?.find((d: any) => d.id === dropId)
          ?.terminalNumber,
      };

      if (type) {
        payload[type] = value;
      }

      await updateDropMutation.mutateAsync({
        dropId,
        data: payload,
      });
    } catch (err) {
      toast.error("Failed to update drop");
    }
  };


  const handleDeleteDrop = (dropId: string) => {
    deleteDrop.mutate(dropId, {
      onSuccess: () => {
        refetchLane();
      },
    });
  }

  const carrierOptions = useMemo(
    () =>
      carrierData?.map((carrier: any) => ({
        label: (
          <div>
            {carrier.carrierName} -{" "}
            <span className="carrier-number">
              {carrier.carrierNumber}
            </span>
          </div>
        ),
        value: carrier.carrierNumber
      })) ?? [],
    [carrierData]
  );

  return (
    <SlideOutSidebar
      show={isOpen}
      onClose={onClose}
      header={data?.lane?.turnNumber || ""}
      size="small-605"
      className="filter-sidebar"
    >

      {isLoading ? (
        <Loading isLoading={true} />
      ) : (

        <Formik
          initialValues={{
            division: data?.lane?.division || "",
            dayOfWeek: data?.lane?.dayOfWeek?.toString() || "",
            description: data?.lane?.turnDescription || "",
            lane: data?.lane?.turnNumber || "",
            carrier: data?.lane?.carrierNumber || "",
            notes: data?.lane?.notes || "",
            drops: data?.drops?.map(drop => ({
              id: drop.id,
              tractorNumber: drop.tractorNumber
            }))
          }} onSubmit={(values) => {
            if (!isEditMode) return;
            handleSave(values);
          }}
          enableReinitialize
        >
          {(formikProps: FormikProps<UpdateSidebarFormValues>) => (
            <Form>
              <div className="update-lane_content">
                <div className="update-lane-container-wrapper">
                  <div className='update-lane-title'>{isEditMode ? "Edit" : "View"} Turn Number and Route Information</div>
                  {isEditMode && <div className='update-lane-actions'>
                    <Button className="update-lane-actions__button" type="button" theme={ButtonThemes.gray} size={ButtonSizes.small} onClick={onClose}>Cancel</Button>
                    <Button className="update-lane-actions__button" type="button" theme={ButtonThemes.blue} size={ButtonSizes.small} onClick={handleAddDrop}>Add Drop</Button>
                    <Button className="update-lane-actions__button" type='submit' theme={ButtonThemes.blue} size={ButtonSizes.small} disabled={!formikProps.dirty}>Save</Button>
                  </div>}
                </div>
              </div>
              <div className="update-lane-container">
                <div className="update-lane-grid-item">
                  {isEditMode ? <XGSFormInput
                    type="text"
                    name="division"
                    labelMode={LabelModes.column}
                    label="Division"
                    readOnly={!isEditMode}
                    className="add-edit-address-section__width-50"

                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Division</div>
                    <div className='update-lane-item_value'>{data?.lane?.division}</div>
                  </div>}
                  {isEditMode ? <XGSFormSelect
                    label="Carrier"
                    name="carrier"
                    options={carrierOptions}
                    readOnly={!isEditMode}
                    value={
                      formikProps.values.carrier
                        ? carrierOptions.find((option: { label: string; value: string }) => option.value.toString() === formikProps.values.carrier.toString())
                        : null
                    }
                    onValueChange={(option) => {
                      formikProps.setFieldValue("carrier", option?.value ?? "");
                    }}
                    formik
                    className="add-edit-address-section__width-50"
                    labelMode={LabelModes.column}

                  />
                    : <div className="update-lane-item">
                      <div className='update-lane-item_label'>Carrier</div>
                      <div className='update-lane-item_value'>{data?.lane?.carrierNumber ?? 'N/A'}</div>
                    </div>}
                </div>
                <div className="update-lane-grid-item">
                  {isEditMode ? <XGSFormSelect
                    label="Day of Week"
                    name="dayOfWeek"

                    options={weekOptions}
                    readOnly={!isEditMode}
                    value={
                      formikProps.values.dayOfWeek
                        ? weekOptions.find((option) => option.value === formikProps.values.dayOfWeek)
                        : null
                    }
                    onValueChange={(option) => {
                      formikProps.setFieldValue("dayOfWeek", option?.value ?? "");
                    }}
                    formik
                    className="add-edit-address-section__width-50"
                    labelMode={LabelModes.column}

                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Day of Week</div>
                    <div className='update-lane-item_value'>{weekOptions.find((option) => option.value === data?.lane?.dayOfWeek?.toString())?.label}</div>
                  </div>}
                  {isEditMode ? <XGSFormInput
                    type="text"
                    name="lane"
                    required={true}
                    labelMode={LabelModes.column}
                    label="Lane"
                    className="add-edit-address-section__width-50"

                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Lane</div>
                    <div className='update-lane-item_value'>{data?.lane?.turnNumber}</div>
                  </div>}
                </div>
                <div className="update-lane-grid-item">
                  {isEditMode ? <XGSFormInput
                    type="text"
                    name="description"

                    labelMode={LabelModes.column}
                    label="Description"
                    className="add-edit-address-section__width-50"

                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Description</div>
                    <div className='update-lane-item_value'>{data?.lane?.turnDescription ?? 'N/A'}</div>
                  </div>}
                </div>
                <div>
                  {isEditMode ? <XGSFormTextarea
                    name="notes"
                    label="Notes"
                    required={false}
                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Notes</div>
                    <div className='update-lane-item_value'>{data?.lane?.notes ?? 'N/A'}</div>
                  </div>}
                </div>

              </div>
              <div style={{ paddingBottom: "14px" }} />
              <Table
                columns={getColumnsForDrops(terminalMap, isEditMode, onSaveDrop, handleDeleteDrop)}
                data={data?.drops || []}
                infiniteScroll
                rowHeight={56}
              />
            </Form>
          )}
        </Formik>
      )
      }

    </SlideOutSidebar >
  );
};

export default UpdateSidebar;
